/**
 * Every endpoint the producer cabinet is allowed to call, and no others.
 *
 * This is an allowlist, deliberately. The producer session is rejected by the backend on
 * `/admin/*` and `/catalog/*` anyway (RBAC is deny-by-default server-side), but not having those
 * functions in the app means nobody can accidentally wire a buyer/admin DTO into a producer
 * screen and only discover it in a 403 — or worse, discover it works because a role check was
 * missed. The client and the server agree on the shape of the hole.
 *
 * Paths and return types come from `backend/api/openapi.yaml`; the types are the narrowed
 * producer-audience ones from `contract.ts`.
 */
import { request, upload } from './http';
import type {
  AppConfig,
  Category,
  Certificate,
  Me,
  Media,
  Notification,
  NotificationPage,
  Paged,
  ProducerOrderListItem,
  ProducerOrderSlice,
  ProducerProduct,
  ProducerProductListItem,
  ProducerProfile,
  ProducerProfileUpdate,
  ProductInput,
  ProductStatus,
  TelegramLinkToken,
  TelegramStatus,
  Locale,
} from './contract';

// ── Session / bootstrap ─────────────────────────────────────────────────────────────────────
export const getMe = () => request<Me>('/me');

export const updateLocale = (locale: Locale) =>
  request<Me>('/me', { method: 'PATCH', body: { locale } });

export const logout = () => request<void>('/auth/logout', { method: 'POST' });

/**
 * Server-authoritative upload limits and truck constants. The wizard's client-side file checks
 * mirror THESE, not hardcoded numbers, so the client cannot drift from the server (docs/06 §10).
 */
export const getConfig = () => request<AppConfig>('/config');

// ── Categories ──────────────────────────────────────────────────────────────────────────────
/**
 * The wizard's category picker needs `{id, slug, name_ru/en/tk}`, and only a producer-scoped
 * door may serve it. `GET /catalog/categories` is buyer-only and MUST stay that way — the buyer
 * catalog group serves SELLING prices, so a producer admitted there could subtract its own base
 * price and read Merdan's margin off the screen. `/admin/categories` is admin-only, and
 * `/public/categories` deliberately omits `id`.
 *
 * `GET /producer/categories` returns the same DTO through the producer guard (see the note in
 * `backend/cmd/most/routes.go`): same category list, different door, and the door is the point.
 */
export const listCategories = () =>
  request<{ items: Category[] }>('/producer/categories').then((r) => r.items);

// ── PRO-2 Profile ───────────────────────────────────────────────────────────────────────────
export const getProfile = () => request<ProducerProfile>('/producer/profile');

/** `markets` is a FULL REPLACE array. A duplicate (country, type) pair is a 422. */
export const updateProfile = (body: ProducerProfileUpdate) =>
  request<ProducerProfile>('/producer/profile', { method: 'PUT', body });

/** Moves the account to `pending_approval` and CLEARS any previous `rejection_reason`. */
export const submitProfile = () =>
  request<{ status: ProducerProfile['approval_status'] }>('/producer/profile/submit', {
    method: 'POST',
  });

export const uploadCertificate = (
  title: string,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
) => {
  const fd = new FormData();
  fd.append('title', title);
  fd.append('file', file);
  return upload<Certificate>('/producer/certificates', fd, onProgress, signal);
};

export const deleteCertificate = (id: string) =>
  request<void>(`/producer/certificates/${id}`, { method: 'DELETE' });

// ── PRO-3 / PRO-4 Products ──────────────────────────────────────────────────────────────────
export const listProducts = (params: {
  page?: number;
  per_page?: number;
  status?: ProductStatus;
}) => {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.per_page) q.set('per_page', String(params.per_page));
  if (params.status) q.set('status', params.status);
  const qs = q.toString();
  return request<Paged<ProducerProductListItem>>(`/producer/products${qs ? `?${qs}` : ''}`);
};

export const getProduct = (id: string) => request<ProducerProduct>(`/producer/products/${id}`);

export const createProduct = (body: ProductInput) =>
  request<ProducerProduct>('/producer/products', { method: 'POST', body });

/**
 * THE MARGIN-GUARD ENDPOINT (openapi `updateProduct`). Editing the base price, price unit, or
 * any packaging field of an already-active, priced product re-enters it into the admin pricing
 * queue; if the new per-piece base price is >= the (invisible-to-us) selling price, the server
 * clears the selling price and the product leaves the buyer catalog until MOST re-prices it.
 *
 * The UI must warn about this BEFORE saving — see `MarginGuardWarning`. It must never phrase the
 * warning in terms of margin, and must never reveal the selling price, because we do not have it
 * and must not want it.
 */
export const updateProduct = (id: string, body: ProductInput) =>
  request<ProducerProduct>(`/producer/products/${id}`, { method: 'PUT', body });

/** Activation does NOT make the product buyable — it makes it visible to Merdan for pricing. */
export const activateProduct = (id: string) =>
  request<ProducerProduct>(`/producer/products/${id}/activate`, { method: 'POST' });

export const archiveProduct = (id: string) =>
  request<ProducerProduct>(`/producer/products/${id}/archive`, { method: 'POST' });

export const uploadProductMedia = (
  productId: string,
  file: File,
  kind: 'photo' | 'video',
  position: number | undefined,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
) => {
  const fd = new FormData();
  fd.append('kind', kind);
  if (position !== undefined) fd.append('position', String(position));
  fd.append('file', file);
  return upload<Media>(`/producer/products/${productId}/media`, fd, onProgress, signal);
};

export const reorderProductMedia = (productId: string, mediaId: string, position: number) =>
  request<Media>(`/producer/products/${productId}/media/${mediaId}`, {
    method: 'PATCH',
    body: { position },
  });

/** An active product may not lose its last photo (server-enforced → 422). */
export const deleteProductMedia = (productId: string, mediaId: string) =>
  request<void>(`/producer/products/${productId}/media/${mediaId}`, { method: 'DELETE' });

// ── PRO-5 / PRO-6 Order slices ──────────────────────────────────────────────────────────────
export const listOrders = (params: { page?: number; per_page?: number }) => {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.per_page) q.set('per_page', String(params.per_page));
  const qs = q.toString();
  return request<Paged<ProducerOrderListItem>>(`/producer/orders${qs ? `?${qs}` : ''}`);
};

/** Routed by order NUMBER (`M-2026-0042`), not id — per the contract and the UI's URLs. */
export const getOrderSlice = (number: string) =>
  request<ProducerOrderSlice>(`/producer/orders/${encodeURIComponent(number)}`);

// ── PRO-7 Notifications ─────────────────────────────────────────────────────────────────────
export const listNotifications = (params: { cursor?: string; limit?: number }) => {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return request<NotificationPage>(`/notifications${qs ? `?${qs}` : ''}`);
};

export const markNotificationRead = (id: string) =>
  request<{ read_at: string }>(`/notifications/${id}/read`, { method: 'POST' });

export const markAllNotificationsRead = () =>
  request<{ updated_count: number }>('/notifications/read-all', { method: 'POST' });

export type { Notification };

// ── Telegram (PRO-1 card) ───────────────────────────────────────────────────────────────────
/** Single-use, 10 min TTL. Opening the deep link is what teaches the bot this user's chat_id. */
export const createTelegramLinkToken = () =>
  request<TelegramLinkToken>('/telegram/link-token', { method: 'POST' });

/** Never echoes the chat_id back. */
export const getTelegramStatus = () => request<TelegramStatus>('/telegram/status');

/** Delivery falls back SILENTLY to email + in-app. Unlinking is never an error state. */
export const unlinkTelegram = () => request<void>('/telegram/link', { method: 'DELETE' });
